import React, { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { useAuth } from "@/App";
import axios from "axios";
import CollapsiblePanel from "./CollapsiblePanel";
import SaveToHealthIntelligenceButton from "./SaveToHealthIntelligenceButton";
import { Microscope, TrendingDown, TrendingUp, Sparkles, Loader2, ChevronRight, X } from "lucide-react";
import { pctColorClass } from "@/i18n/nutrientLabels";

/**
 * NutrientDeficiencyPanel — Weekly avg intake vs RDI, highlights top-3 deficits.
 * Shows actionable food suggestions.
 */
export default function NutrientDeficiencyPanel() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const { API } = useAuth();
  const bg = language === "bg";

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [daysRange, setDaysRange] = useState(7);
  const [suggestOpen, setSuggestOpen] = useState(null); // nutrient key
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/nutrition/weekly-deficiency`, {
        params: { days: daysRange, lang: language },
      });
      setData(data);
    } catch {
      setData(null);
    }
    setLoading(false);
  }, [API, daysRange, language]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const openSuggestions = async (nutrientKey) => {
    if (suggestOpen === nutrientKey) {
      setSuggestOpen(null);
      return;
    }
    setSuggestOpen(nutrientKey);
    setSuggestLoading(true);
    try {
      const { data } = await axios.get(`${API}/nutrition/suggest-foods`, {
        params: { nutrient: nutrientKey, limit: 5 },
      });
      setSuggestions(data.foods || []);
    } catch {
      setSuggestions([]);
    }
    setSuggestLoading(false);
  };

  const hasData = data && data.meals_count > 0;
  const deficiencies = data?.deficiencies || [];
  const allNutrients = data?.all_nutrients || [];

  return (
    <CollapsiblePanel
      title={bg ? "Анализ на дефицити" : "Deficiency Analysis"}
      subtitle={bg ? "Седмичен средно-дневен прием vs RDI" : "Weekly avg daily intake vs RDI"}
      icon={Microscope}
      iconColor="#06B6D4"
      credits={0}
      defaultOpen={false}
      storageKey="nutrient-deficiency"
      testId="nutrient-deficiency-panel"
    >
      <div className="space-y-3">
        {/* Days selector — 1 / 7 / 14 / 30 */}
        <div className="flex gap-1.5" data-testid="deficiency-days-tabs">
          {[1, 7, 14, 30].map((d) => (
            <button
              key={d}
              onClick={() => setDaysRange(d)}
              className={`flex-1 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${
                daysRange === d
                  ? "bg-gradient-to-r from-[#06B6D4] to-[#6366F1] text-white shadow-sm"
                  : isDark ? "bg-[hsl(223,47%,10%)] text-gray-400" : "bg-gray-100 text-gray-600"
              }`}
              data-testid={`deficiency-days-${d}`}
            >
              {d} {bg ? (d === 1 ? "ден" : "дни") : (d === 1 ? "day" : "days")}
            </button>
          ))}
        </div>

        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-[#06B6D4]" />
          </div>
        )}

        {!loading && !hasData && (
          <div className={`p-4 rounded-lg text-center ${isDark ? "bg-[hsl(223,47%,10%)]" : "bg-gray-50"}`}>
            <Sparkles className="w-5 h-5 mx-auto mb-2 text-[#06B6D4]" />
            <p className={`text-xs font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              {bg ? "Няма логнати ястия за анализ" : "No logged meals to analyze"}
            </p>
            <p className={`text-[10px] mt-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
              {bg ? "Започнете да записвате храна, за да получите персонален отчет" : "Start logging meals for a personal report"}
            </p>
          </div>
        )}

        {!loading && hasData && (
          <>
            {/* Summary */}
            <div className={`p-2.5 rounded-lg border ${isDark ? "bg-[#06B6D4]/10 border-[#06B6D4]/30" : "bg-cyan-50 border-cyan-200"}`} data-testid="deficiency-summary">
              <p className={`text-[11px] leading-snug ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                {data.summary}
              </p>
              <p className={`text-[9px] mt-1.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                {bg ? `Базирано на ${data.meals_count} ястия от ${data.actual_days_tracked} дни` : `Based on ${data.meals_count} meals across ${data.actual_days_tracked} days`}
              </p>
            </div>

            {/* Top 3 deficiencies */}
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <TrendingDown className="w-3.5 h-3.5 text-red-500" />
                <h4 className={`text-xs font-bold ${isDark ? "text-white" : "text-gray-800"}`}>
                  {bg ? "Топ 3 дефицити" : "Top 3 deficiencies"}
                </h4>
              </div>
              <div className="space-y-1" data-testid="deficiencies-list">
                {deficiencies.map((d) => (
                  <div key={d.key}>
                    <button
                      onClick={() => openSuggestions(d.key)}
                      className={`w-full flex items-center gap-2 p-2 rounded-lg transition-colors ${isDark ? "bg-[hsl(223,47%,10%)] hover:bg-[hsl(223,47%,14%)]" : "bg-gray-50 hover:bg-gray-100"}`}
                      data-testid={`deficiency-item-${d.key}`}
                    >
                      <div className="flex-1 min-w-0 text-left">
                        <p className={`text-xs font-semibold truncate ${isDark ? "text-gray-200" : "text-gray-800"}`}>{d.label}</p>
                        <p className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                          {d.daily_avg}{d.unit} {bg ? "от" : "of"} {d.rdi}{d.unit}
                        </p>
                      </div>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${pctColorClass(d.pct_rdi, isDark)}`}>
                        {d.pct_rdi}%
                      </span>
                      <ChevronRight className={`w-3.5 h-3.5 transition-transform ${suggestOpen === d.key ? "rotate-90" : ""} ${isDark ? "text-gray-500" : "text-gray-400"}`} />
                    </button>

                    {suggestOpen === d.key && (
                      <div className={`ml-2 mt-1.5 pl-2 border-l-2 ${isDark ? "border-[#06B6D4]/40" : "border-cyan-300"}`} data-testid={`deficiency-suggestions-${d.key}`}>
                        <p className={`text-[10px] font-semibold mb-1 ${isDark ? "text-[#06B6D4]" : "text-cyan-700"}`}>
                          {bg ? "Богати на" : "Rich in"} {d.label}:
                        </p>
                        {suggestLoading ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <div className="space-y-0.5">
                            {suggestions.map((s) => (
                              <div key={s.fdc_id} className={`p-1.5 rounded text-[10px] flex items-center justify-between ${isDark ? "bg-[hsl(223,47%,8%)]" : "bg-white"}`}>
                                <p className={`flex-1 truncate ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                                  {s.name_bg || s.name}
                                </p>
                                <span className="text-emerald-500 font-bold ml-1.5">{s.pct_rdi_per_100g}%</span>
                              </div>
                            ))}
                            {suggestions.length === 0 && (
                              <p className={`text-[9px] italic ${isDark ? "text-gray-500" : "text-gray-400"}`}>{bg ? "Няма предложения." : "No suggestions."}</p>
                            )}
                          </div>
                        )}
                        <button
                          onClick={() => setSuggestOpen(null)}
                          className={`mt-1 flex items-center gap-0.5 text-[9px] ${isDark ? "text-gray-500" : "text-gray-400"}`}
                        >
                          <X className="w-2.5 h-2.5" />
                          {bg ? "Затвори" : "Close"}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* All nutrients compact grid */}
            <details className={`rounded-lg ${isDark ? "bg-[hsl(223,47%,10%)]" : "bg-gray-50"}`}>
              <summary className={`px-2.5 py-1.5 text-[10px] font-semibold cursor-pointer ${isDark ? "text-gray-400" : "text-gray-600"}`} data-testid="deficiency-all-nutrients-summary">
                {bg ? `Всички ${allNutrients.length} нутриента` : `All ${allNutrients.length} nutrients`}
              </summary>
              <div className="p-2 space-y-0.5">
                {allNutrients.map((n) => (
                  <div key={n.key} className="flex items-center gap-2 text-[10px]">
                    <p className={`flex-1 truncate ${isDark ? "text-gray-400" : "text-gray-600"}`}>{n.label}</p>
                    <span className={`font-mono ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                      {n.daily_avg}{n.unit}
                    </span>
                    <span className={`px-1 py-0.5 rounded font-semibold min-w-[32px] text-center ${pctColorClass(n.pct_rdi, isDark)}`}>
                      {n.pct_rdi}%
                    </span>
                  </div>
                ))}
              </div>
            </details>

            <p className={`text-[9px] italic ${isDark ? "text-gray-600" : "text-gray-400"}`}>
              {bg
                ? "Анализ на приоритетни 11 нутриента. % RDI базирано на US FDA Daily Values."
                : "Analyzes 11 priority nutrients. %RDI based on US FDA Daily Values."}
            </p>

            {/* iter548 — Save deficiency snapshot to Health Intelligence */}
            <SaveToHealthIntelligenceButton
              snapshotType="daily_summary"
              size="sm"
              title={bg
                ? `Анализ на дефицити — ${daysRange} дни`
                : `Deficiency analysis — ${daysRange} days`}
              summary={data.summary}
              payload={{
                source: "nutrient_deficiency",
                module: "nutrition",
                days_range: daysRange,
                meals_count: data.meals_count,
                actual_days_tracked: data.actual_days_tracked,
                top_deficiencies: deficiencies.slice(0, 3).map((d) => ({
                  key: d.key, label: d.label, pct_rdi: d.pct_rdi,
                  daily_avg: d.daily_avg, rdi: d.rdi, unit: d.unit,
                })),
              }}
              tags={["nutrient-deficiency", `range-${daysRange}d`]}
            />
          </>
        )}
      </div>
    </CollapsiblePanel>
  );
}
